package com.fantasytenis.web.screens.gameday

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.core.realtime.LiveScorePayload
import com.fantasytenis.shared.domain.contests.entity.ContestEntity
import com.fantasytenis.shared.domain.contests.entity.ContestStatus
import com.fantasytenis.shared.domain.contests.entity.EntryEntity
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.lineup.entity.LineupEntity
import com.fantasytenis.shared.domain.live.entity.ScoreEvent
import com.fantasytenis.shared.domain.live.entity.EventType
import com.fantasytenis.shared.domain.live.entity.PreviousMatchStats
import com.fantasytenis.shared.domain.scoring.ScoreCalculator
import com.fantasytenis.shared.domain.scoring.entity.LeaderboardEntry
import com.fantasytenis.shared.domain.tennis.entity.TennisPlayerEntity
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.CelebrationEvent
import com.fantasytenis.web.components.CelebrationManager
import com.fantasytenis.web.components.CelebrationType
import com.fantasytenis.web.components.MiniLeaderboardCard
import com.fantasytenis.web.components.RankChangeEvent
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.components.SweatMeter
import com.fantasytenis.web.components.SweatMeterCompact
import com.fantasytenis.web.components.lineup.MatchupBadge
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.hooks.isMobile
import com.fantasytenis.web.hooks.useContestSubscription
import com.fantasytenis.web.hooks.useRealtime
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.theme.ThemeColors
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private val console: dynamic get() = window.asDynamic().console

/**
 * Game Day Screen - Live contest experience showing:
 * 1. Lineup overview with real-time category score breakdowns
 * 2. Live event feed with animations
 * 3. Leaderboard context showing rank and payout proximity
 */
@Composable
fun GameDayScreen(contestId: String, entryId: String) {
    val scope = rememberCoroutineScope()
    val colors = Theme.colors
    val isMobileView = isMobile()
    val currentUserId = SharedData.currentUser.collectAsState().value?.id

    // State
    var contest by remember { mutableStateOf<ContestEntity?>(null) }
    var entry by remember { mutableStateOf<EntryEntity?>(null) }
    var lineup by remember { mutableStateOf<LineupEntity?>(null) }
    var players by remember { mutableStateOf<Map<String, TennisPlayerEntity>>(emptyMap()) }
    var leaderboardEntries by remember { mutableStateOf<List<LeaderboardEntry>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    // Track score events for the feed
    val scoreEvents by SharedData.scoreEvents.collectAsState()
    val liveMatches by SharedData.liveMatches.collectAsState()
    val liveScorePayloads by SharedData.liveScorePayloads.collectAsState()

    // Track previous scores for animation
    var previousCategoryScores by remember { mutableStateOf<Map<Category, Double>>(emptyMap()) }

    // Animation states
    var animatingCategories by remember { mutableStateOf<Set<Category>>(emptySet()) }

    // Celebration queue for major events
    var celebrationQueue by remember { mutableStateOf<List<CelebrationEvent>>(emptyList()) }

    // Track payout status for celebration triggers
    var wasInPayout by remember { mutableStateOf<Boolean?>(null) }


    // Initialize realtime connection
    val realtimeState = useRealtime()

    // Subscribe to contest updates for leaderboard
    useContestSubscription(
        contestId = contestId,
        onLeaderboardUpdate = { update ->
            val updatedEntries = leaderboardEntries.toMutableList()
            update.entries.forEach { liveEntry ->
                val index = updatedEntries.indexOfFirst { it.userId == liveEntry.userId }
                if (index >= 0) {
                    updatedEntries[index] = LeaderboardEntry(
                        userId = liveEntry.userId,
                        userName = liveEntry.displayName,
                        rank = liveEntry.rank,
                        previousRank = liveEntry.previousRank,
                        points = liveEntry.totalPoints,
                        isCurrentUser = updatedEntries[index].isCurrentUser,
                        payout = liveEntry.payoutAmount
                    )
                }
            }
            leaderboardEntries = updatedEntries.sortedBy { it.rank }
        },
        onEntryUpdate = { update ->
            if (update.userId == currentUserId) {
                // Update our entry's rank and points
                entry = entry?.copy(
                    totalPoints = update.totalPoints,
                    rank = update.rank
                )
            }
        }
    )

    // Load initial data
    LaunchedEffect(contestId, entryId) {
        isLoading = true
        error = null

        // Load contest
        DependencyContainer.getContestUseCase(contestId).fold(
            onSuccess = { contest = it },
            onFailure = { error = it.message }
        )

        // Load leaderboard to find entry and get standings
        DependencyContainer.getContestLeaderboardUseCase(contestId, limit = 100).fold(
            onSuccess = { entries ->
                entry = entries.find { it.id == entryId }
                leaderboardEntries = entries.mapIndexed { index, e ->
                    LeaderboardEntry(
                        userId = e.userId,
                        userName = e.formattedDisplayName,
                        rank = e.rank ?: (index + 1),
                        previousRank = e.rank ?: (index + 1),
                        points = e.totalPoints,
                        isCurrentUser = e.userId == currentUserId,
                        payout = e.payoutAmount,
                        username = e.username,
                        avatarUrl = e.avatarUrl,
                        entryId = e.id
                    )
                }
            },
            onFailure = { error = it.message }
        )

        // Load lineup
        entry?.lineupId?.let { lineupId ->
            DependencyContainer.getLineupUseCase(lineupId).fold(
                onSuccess = { lineup = it },
                onFailure = { /* Lineup may not exist */ }
            )
        }

        // Load slate players
        contest?.let { c ->
            DependencyContainer.getSlatePlayersUseCase(c.tournamentId, "M").fold(
                onSuccess = { playerList ->
                    players = playerList.associateBy { it.id }
                },
                onFailure = { /* Not critical */ }
            )
        }

        isLoading = false
    }

    // Detect score changes and trigger animations
    LaunchedEffect(lineup?.categoryScores) {
        lineup?.categoryScores?.let { scores ->
            val newScores = mapOf(
                Category.SERVE to scores.serve,
                Category.FOREHAND to scores.forehand,
                Category.BACKHAND to scores.backhand,
                Category.VOLLEYS to scores.volleys,
                Category.FIGHTING to scores.fighting
            )

            // Check for changes
            Category.entries.forEach { category ->
                val prev = previousCategoryScores[category] ?: 0.0
                val curr = newScores[category] ?: 0.0
                if (curr != prev && previousCategoryScores.isNotEmpty()) {
                    // Trigger animation
                    animatingCategories = animatingCategories + category
                    scope.launch {
                        delay(500) // Animation duration
                        animatingCategories = animatingCategories - category
                    }
                }
            }

            previousCategoryScores = newScores
        }
    }

    // Detect payout zone entry/exit for celebrations
    LaunchedEffect(entry?.rank, contest?.prizeStructure) {
        val currentRank = entry?.rank ?: return@LaunchedEffect
        val payoutThreshold = contest?.prizeStructure?.maxOfOrNull { it.maxRank } ?: return@LaunchedEffect
        val isInPayout = currentRank in 1..payoutThreshold

        // Check if we just entered the payout zone
        if (wasInPayout == false && isInPayout) {
            celebrationQueue = celebrationQueue + CelebrationEvent(
                type = CelebrationType.ENTERED_PAYOUT,
                customSubtitle = "You're now in position #$currentRank"
            )
        }

        wasInPayout = isInPayout
    }

    // Detect major scoring events for celebrations
    LaunchedEffect(scoreEvents) {
        // Check latest event for celebration-worthy moments
        val latestEvent = scoreEvents.firstOrNull() ?: return@LaunchedEffect

        // Get lineup player IDs
        val lineupPlayerIds = listOfNotNull(
            lineup?.slots?.serve,
            lineup?.slots?.forehand,
            lineup?.slots?.backhand,
            lineup?.slots?.volleys,
            lineup?.slots?.fighting
        )

        // Only celebrate if it's one of our players
        if (latestEvent.playerId !in lineupPlayerIds) return@LaunchedEffect

        when (latestEvent.eventType) {
            EventType.ACE -> {
                celebrationQueue = celebrationQueue + CelebrationEvent(
                    type = CelebrationType.ACE,
                    playerName = latestEvent.playerName
                )
            }
            EventType.BREAK_POINT_CONVERTED -> {
                celebrationQueue = celebrationQueue + CelebrationEvent(
                    type = CelebrationType.BREAK_CONVERTED,
                    playerName = latestEvent.playerName
                )
            }
            EventType.MATCH_WON -> {
                celebrationQueue = celebrationQueue + CelebrationEvent(
                    type = CelebrationType.MATCH_WIN,
                    playerName = latestEvent.playerName
                )
            }
            else -> {} // Don't celebrate other events
        }
    }

    // Handle rank change callback from MiniLeaderboardCard
    fun handleRankChange(event: RankChangeEvent) {
        if (event.isCurrentUser && event.newRank < event.oldRank) {
            // User moved up - find who they passed
            val passedEntry = leaderboardEntries.find { it.rank == event.oldRank }
            celebrationQueue = celebrationQueue + CelebrationEvent(
                type = CelebrationType.PASSED_OPPONENT,
                customSubtitle = passedEntry?.let { "You passed ${it.displayName}!" }
                    ?: "Moved up to #${event.newRank}!"
            )
        }
    }

    // Celebration overlay manager
    CelebrationManager(
        celebrations = celebrationQueue,
        onCelebrationDismissed = { id ->
            celebrationQueue = celebrationQueue.filter { it.id != id }
        },
        colors = colors,
        useFullScreen = true
    )

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(colors.background)
        }
    }) {
        // Header
        ResponsiveHeader(
            title = contest?.name ?: "Game Day",
            showBackButton = true,
            onBackClick = { NavigationState.navigate(Route.ContestDetail(contestId)) },
            customActions = {
                // Live indicator
                if (realtimeState.isConnected && contest?.status == ContestStatus.LIVE) {
                    LiveIndicator()
                }
            }
        )

        // Main content
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            Div({
                style {
                    padding(if (isMobileView) 16.px else 24.px)
                    maxWidth(1000.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
                when {
                    isLoading -> LoadingState(colors)
                    error != null -> ErrorState(error!!, colors)
                    contest != null && lineup != null -> {
                        // Calculate payout data for SweatMeter
                        val currentRank = entry?.rank ?: 0
                        val currentPoints = entry?.totalPoints ?: 0.0
                        val payoutThreshold = contest!!.prizeStructure.maxOfOrNull { it.maxRank } ?: 0
                        val inPayout = currentRank in 1..payoutThreshold

                        // Points to payout threshold entry
                        val payoutThresholdEntry = leaderboardEntries.find { it.rank == payoutThreshold }
                        val pointsToPayout = if (!inPayout && payoutThresholdEntry != null) {
                            payoutThresholdEntry.points - currentPoints
                        } else if (inPayout && payoutThresholdEntry != null) {
                            currentPoints - payoutThresholdEntry.points // Buffer above payout line
                        } else 0.0

                        // Points to next rank
                        val entryAbove = leaderboardEntries.find { it.rank == currentRank - 1 }
                        val pointsToNextRank = entryAbove?.let { it.points - currentPoints } ?: 0.0

                        // SweatMeter - main engagement widget
                        SweatMeter(
                            currentRank = currentRank,
                            currentPoints = currentPoints,
                            payoutThreshold = payoutThreshold,
                            pointsToPayout = pointsToPayout,
                            pointsToNextRank = pointsToNextRank,
                            totalEntries = contest!!.currentEntries,
                            colors = colors
                        )

                        // Mini leaderboard showing nearby entries
                        if (leaderboardEntries.isNotEmpty()) {
                            Div({ style { marginTop(16.px) } }) {
                                MiniLeaderboardCard(
                                    entries = getNeighboringEntries(leaderboardEntries, currentUserId, count = 5),
                                    focusedUserId = currentUserId,
                                    contestId = contestId,
                                    isLive = realtimeState.isConnected && contest?.status == ContestStatus.LIVE,
                                    onEntryClick = { clickedEntryId ->
                                        NavigationState.navigate(Route.ContestEntryView(contestId, clickedEntryId))
                                    },
                                    onViewFullClick = {
                                        NavigationState.navigate(Route.ContestLeaderboard(contestId))
                                    },
                                    onRankChange = { event -> handleRankChange(event) },
                                    colors = colors
                                )
                            }
                        }

                        // Lineup grid
                        Div({ style { marginTop(20.px) } }) {
                            LineupOverview(
                                lineup = lineup!!,
                                players = players,
                                liveMatches = liveMatches,
                                liveScorePayloads = liveScorePayloads,
                                animatingCategories = animatingCategories,
                                colors = colors,
                                isMobile = isMobileView
                            )
                        }

                        // Live event feed
                        if (scoreEvents.isNotEmpty()) {
                            Div({ style { marginTop(24.px) } }) {
                                LiveEventFeed(
                                    events = scoreEvents.take(20),
                                    colors = colors
                                )
                            }
                        }
                    }
                    else -> {
                        NoDataState(colors)
                    }
                }
            }
        }
    }
}

@Composable
private fun LoadingState(colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, colors.primary)
                borderRadius(50.percent)
                property("border-top-color", "transparent")
                property("animation", "spin 1s linear infinite")
            }
        })
    }
}

@Composable
private fun ErrorState(message: String, colors: ThemeColors) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(colors.error)
                fontSize(16.px)
            }
        }) {
            Text("Error: $message")
        }
    }
}

@Composable
private fun NoDataState(colors: ThemeColors) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(colors.textSecondary)
                fontSize(16.px)
            }
        }) {
            Text("No lineup data available")
        }
    }
}

@Composable
private fun LiveIndicator() {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            backgroundColor(Color("rgba(76, 175, 80, 0.2)"))
            padding(4.px, 10.px)
            borderRadius(12.px)
        }
    }) {
        Div({
            classes("game-day-live-indicator")
            style {
                width(8.px)
                height(8.px)
                borderRadius(50.percent)
                backgroundColor(Colors.Success)
            }
        })
        Span({
            style {
                fontSize(11.px)
                color(Colors.White)
                fontWeight(600)
            }
        }) {
            Text("LIVE")
        }
    }
}

@Composable
private fun LeaderboardContextWidget(
    entry: EntryEntity?,
    contest: ContestEntity,
    leaderboardEntries: List<LeaderboardEntry>,
    colors: ThemeColors
) {
    val currentRank = entry?.rank ?: 0
    val totalEntries = contest.currentEntries.coerceAtLeast(1)
    val currentPoints = entry?.totalPoints ?: 0.0

    // Find the entry above and payout threshold
    val entryAbove = leaderboardEntries.find { it.rank == currentRank - 1 }
    val pointsToNextRank = entryAbove?.let { it.points - currentPoints } ?: 0.0

    // Find payout threshold
    val payoutThreshold = contest.prizeStructure.maxOfOrNull { it.maxRank } ?: 0
    val inPayout = currentRank <= payoutThreshold && currentRank > 0

    Div({
        classes("game-day-leaderboard-context")
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(16.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            flexWrap(FlexWrap.Wrap)
            gap(12.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
            if (inPayout) {
                property("border", "2px solid ${Colors.Success}")
            }
        }
    }) {
        // Rank section
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            // Rank badge
            Div({
                style {
                    backgroundColor(
                        when (currentRank) {
                            1 -> Colors.Medal.GoldBackground
                            2 -> Colors.Medal.SilverBackground
                            3 -> Colors.Medal.BronzeBackground
                            else -> colors.surfaceVariant
                        }
                    )
                    color(
                        when (currentRank) {
                            1 -> Colors.Medal.GoldText
                            2 -> Colors.Medal.SilverText
                            3 -> Colors.Medal.BronzeText
                            else -> colors.textPrimary
                        }
                    )
                    width(48.px)
                    height(48.px)
                    borderRadius(50.percent)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    fontSize(18.px)
                    fontWeight(700)
                }
            }) {
                Text("#$currentRank")
            }

            Div {
                Div({
                    style {
                        fontSize(13.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("of $totalEntries entries")
                }
                if (pointsToNextRank > 0 && currentRank > 1) {
                    Div({
                        style {
                            fontSize(12.px)
                            color(Colors.Warning)
                            fontWeight(500)
                        }
                    }) {
                        Text("${pointsToNextRank.asDynamic().toFixed(1)} pts from #${currentRank - 1}")
                    }
                }
            }
        }

        // Points section
        Div({
            style {
                textAlign("right")
            }
        }) {
            Div({
                style {
                    fontSize(11.px)
                    color(colors.textSecondary)
                    fontWeight(600)
                    letterSpacing(0.5.px)
                    marginBottom(2.px)
                }
            }) {
                Text("TOTAL POINTS")
            }
            Div({
                style {
                    fontSize(28.px)
                    fontWeight(700)
                    color(colors.primary)
                }
            }) {
                Text("${currentPoints.asDynamic().toFixed(1)}")
            }
        }

        // Payout indicator
        if (payoutThreshold > 0) {
            Div({
                style {
                    width(100.percent)
                    marginTop(8.px)
                    paddingTop(12.px)
                    property("border-top", "1px solid ${colors.border}")
                }
            }) {
                if (inPayout) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                            color(Colors.Success)
                            fontWeight(600)
                            fontSize(14.px)
                        }
                    }) {
                        Span { Text("\uD83C\uDFC6") } // Trophy emoji
                        Text("In the money! (Top $payoutThreshold pays)")
                    }
                } else {
                    val pointsToPayout = leaderboardEntries
                        .find { it.rank == payoutThreshold }
                        ?.let { it.points - currentPoints }
                        ?: 0.0

                    Div({
                        style {
                            fontSize(13.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text("${pointsToPayout.asDynamic().toFixed(1)} pts from payout (Top $payoutThreshold)")
                    }
                }
            }
        }
    }
}

@Composable
private fun LineupOverview(
    lineup: LineupEntity,
    players: Map<String, TennisPlayerEntity>,
    liveMatches: Map<String, com.fantasytenis.shared.domain.scoring.entity.LiveScoreEntity>,
    liveScorePayloads: Map<String, LiveScorePayload>,
    animatingCategories: Set<Category>,
    colors: ThemeColors,
    isMobile: Boolean
) {
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(16.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        H3({
            style {
                color(colors.textPrimary)
                fontSize(16.px)
                fontWeight(700)
                marginTop(0.px)
                marginBottom(16.px)
            }
        }) {
            Text("Your Lineup")
        }

        val categories = listOf(
            Category.SERVE to lineup.slots.serve,
            Category.FOREHAND to lineup.slots.forehand,
            Category.BACKHAND to lineup.slots.backhand,
            Category.VOLLEYS to lineup.slots.volleys,
            Category.FIGHTING to lineup.slots.fighting
        )

        Div({
            classes("game-day-lineup-grid")
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", if (isMobile) "1fr" else "repeat(2, 1fr)")
                gap(12.px)
            }
        }) {
            categories.forEach { (category, playerId) ->
                val player = playerId?.let { players[it] }
                val categoryScore = lineup.categoryScores?.getScoreForCategory(category) ?: 0.0
                val isAnimating = category in animatingCategories

                // Find the match for this player
                val playerMatch = playerId?.let { pid ->
                    liveMatches.values.find { match ->
                        match.player1.playerId == pid || match.player2.playerId == pid
                    }
                }
                val matchPayload = playerMatch?.let { liveScorePayloads[it.matchId] }

                LineupPlayerCard(
                    category = category,
                    player = player,
                    categoryScore = categoryScore,
                    matchScore = playerMatch?.let { formatMatchScore(it) },
                    matchStatus = playerMatch?.status?.name,
                    isLive = playerMatch?.status == com.fantasytenis.shared.domain.scoring.entity.LiveMatchStatus.IN_PROGRESS,
                    isAnimating = isAnimating,
                    matchPayload = matchPayload,
                    colors = colors
                )
            }
        }

        // Total
        Div({
            style {
                marginTop(16.px)
                paddingTop(16.px)
                property("border-top", "1px solid ${colors.border}")
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Span({
                style {
                    fontWeight(700)
                    color(colors.textPrimary)
                }
            }) {
                Text("Total")
            }
            Span({
                style {
                    fontWeight(700)
                    fontSize(20.px)
                    color(colors.primary)
                }
            }) {
                Text("${lineup.totalPoints.asDynamic().toFixed(1)} pts")
            }
        }
    }
}

@Composable
private fun LineupPlayerCard(
    category: Category,
    player: TennisPlayerEntity?,
    categoryScore: Double,
    matchScore: String?,
    matchStatus: String?,
    isLive: Boolean,
    isAnimating: Boolean,
    matchPayload: LiveScorePayload?,
    colors: ThemeColors
) {
    val categoryColor = when (category) {
        Category.SERVE -> Colors.Category.Serve
        Category.FOREHAND -> Colors.Category.Forehand
        Category.BACKHAND -> Colors.Category.Backhand
        Category.VOLLEYS -> Colors.Category.Volleys
        Category.FIGHTING -> Colors.Category.Fighting
    }

    val categoryIcon = when (category) {
        Category.SERVE -> "\uD83C\uDFBE" // Tennis ball
        Category.FOREHAND -> "\uD83D\uDCAA" // Flexed biceps
        Category.BACKHAND -> "\uD83C\uDFAF" // Target
        Category.VOLLEYS -> "\uD83C\uDFD0" // Volleyball
        Category.FIGHTING -> "\uD83D\uDD25" // Fire
    }

    Div({
        classes("game-day-player-card")
        if (isAnimating) classes("game-day-score-up")
        if (isLive) classes("game-day-category-active")
        style {
            backgroundColor(colors.surfaceVariant)
            borderRadius(10.px)
            padding(14.px)
            property("border-left", "4px solid $categoryColor")
            position(Position.Relative)
            overflow("hidden")
        }
    }) {
        // Header: Category + Score
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(10.px)
            }
        }) {
            // Category badge
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                }
            }) {
                Span({ style { fontSize(16.px) } }) { Text(categoryIcon) }
                Span({
                    style {
                        fontSize(12.px)
                        fontWeight(700)
                        color(categoryColor)
                        property("text-transform", "uppercase")
                        letterSpacing(0.5.px)
                    }
                }) {
                    Text(category.displayName)
                }
            }

            // Score
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                }
            }) {
                Span({
                    style {
                        fontSize(20.px)
                        fontWeight(700)
                        color(if (categoryScore > 0) colors.primary else colors.textSecondary)
                    }
                }) {
                    Text("${categoryScore.asDynamic().toFixed(1)}")
                }
                Span({
                    style {
                        fontSize(12.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("pts")
                }
            }
        }

        // Player info
        if (player != null) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                Div {
                    Div({
                        style {
                            fontWeight(600)
                            color(colors.textPrimary)
                            marginBottom(2.px)
                        }
                    }) {
                        Text(player.name)
                    }

                    // Match score
                    if (matchScore != null) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(6.px)
                                fontSize(13.px)
                                color(colors.textSecondary)
                            }
                        }) {
                            Text(matchScore)
                            if (isLive) {
                                Span({
                                    classes("game-day-live-indicator")
                                    style {
                                        width(6.px)
                                        height(6.px)
                                        borderRadius(50.percent)
                                        backgroundColor(Colors.Live)
                                        marginLeft(4.px)
                                    }
                                })
                            }
                        }
                    } else if (matchStatus == null) {
                        // Match not started - show opponent and scheduled time
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(8.px)
                                flexWrap(FlexWrap.Wrap)
                            }
                        }) {
                            if (player.opponentName != null) {
                                // Opponent name
                                Span({
                                    style {
                                        fontSize(12.px)
                                        color(colors.textSecondary)
                                    }
                                }) {
                                    Text("vs ${formatOpponentName(player.opponentName!!)}")
                                }

                                // Matchup badge
                                MatchupBadge(
                                    playerRanking = player.atpRanking,
                                    opponentRanking = player.opponentRanking
                                )

                                // Match time
                                player.matchTime?.let { time ->
                                    Span({
                                        style {
                                            fontSize(11.px)
                                            color(colors.textSecondary)
                                        }
                                    }) {
                                        Text(formatMatchTime(time))
                                    }
                                }
                            } else {
                                // Opponent TBA
                                Span({
                                    style {
                                        fontSize(12.px)
                                        color(colors.textSecondary)
                                        fontStyle("italic")
                                    }
                                }) {
                                    Text("Opponent TBA")
                                }
                            }
                        }
                    }
                }

                // Quick stat preview from payload
                matchPayload?.let { payload ->
                    val relevantStat = when (category) {
                        Category.SERVE -> "${payload.player1Aces + payload.player2Aces} aces"
                        Category.FIGHTING -> "${payload.player1BreakPointsWon + payload.player2BreakPointsWon} BP conv"
                        else -> null
                    }
                    relevantStat?.let { stat ->
                        Span({
                            style {
                                fontSize(11.px)
                                color(colors.textSecondary)
                                backgroundColor(colors.background)
                                padding(2.px, 6.px)
                                borderRadius(4.px)
                            }
                        }) {
                            Text(stat)
                        }
                    }
                }
            }
        } else {
            Div({
                style {
                    color(colors.textSecondary)
                    fontStyle("italic")
                }
            }) {
                Text("Empty slot")
            }
        }
    }
}

@Composable
private fun LiveEventFeed(
    events: List<ScoreEvent>,
    colors: ThemeColors
) {
    Div({
        classes("game-day-event-feed")
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(16.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
            maxHeight(300.px)
            overflowY("auto")
        }
    }) {
        H4({
            style {
                color(colors.textPrimary)
                fontSize(14.px)
                fontWeight(700)
                marginTop(0.px)
                marginBottom(12.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
            }
        }) {
            Text("Live Feed")
            Div({
                classes("game-day-live-indicator")
                style {
                    width(6.px)
                    height(6.px)
                    borderRadius(50.percent)
                    backgroundColor(Colors.Live)
                }
            })
        }

        if (events.isEmpty()) {
            Div({
                style {
                    textAlign("center")
                    padding(24.px)
                    color(colors.textSecondary)
                    fontSize(14.px)
                }
            }) {
                Text("Waiting for scoring events...")
            }
        } else {
            events.forEachIndexed { index, event ->
                EventRow(
                    event = event,
                    isNew = index == 0,
                    colors = colors
                )
            }
        }
    }
}

@Composable
private fun EventRow(
    event: ScoreEvent,
    isNew: Boolean,
    colors: ThemeColors
) {
    val eventTypeInfo = event.eventType
    val eventClass = when {
        eventTypeInfo.isMajorEvent -> "event-major"
        eventTypeInfo.isPositive -> "event-positive"
        else -> "event-negative"
    }

    Div({
        classes(eventClass)
        if (isNew) classes("game-day-event-new")
        style {
            padding(10.px, 12.px)
            marginBottom(8.px)
            borderRadius(8.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(10.px)
        }
    }) {
        // Event icon
        Span({ style { fontSize(16.px) } }) {
            Text(eventTypeInfo.emoji)
        }

        // Event details
        Div({
            style {
                flex(1)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                }
            }) {
                Span({
                    style {
                        fontWeight(600)
                        color(colors.textPrimary)
                        fontSize(13.px)
                    }
                }) {
                    Text(event.playerName)
                }
                Span({
                    style {
                        color(colors.textSecondary)
                        fontSize(12.px)
                    }
                }) {
                    Text("- ${eventTypeInfo.displayName}")
                }
            }
            Div({
                style {
                    fontSize(11.px)
                    color(colors.textSecondary)
                }
            }) {
                Text(event.matchContext)
            }
        }

        // Points
        Span({
            style {
                fontWeight(700)
                fontSize(14.px)
                color(if (event.pointsAwarded > 0) Colors.Success else Colors.Error)
                minWidth(45.px)
                textAlign("right")
            }
        }) {
            Text(if (event.pointsAwarded > 0) "+${event.pointsAwarded}" else "${event.pointsAwarded}")
        }
    }
}

private fun formatMatchScore(match: com.fantasytenis.shared.domain.scoring.entity.LiveScoreEntity): String {
    val p1Sets = match.player1.sets.joinToString("-")
    val p2Sets = match.player2.sets.joinToString("-")
    return "$p1Sets ($p2Sets)"
}

/**
 * Get entries surrounding the current user for the mini leaderboard.
 * Centers on the user's position with 2 entries above and 2 below.
 */
private fun getNeighboringEntries(
    entries: List<LeaderboardEntry>,
    userId: String?,
    count: Int = 5
): List<LeaderboardEntry> {
    val sortedEntries = entries.sortedBy { it.rank }
    val index = sortedEntries.indexOfFirst { it.userId == userId }
    if (index < 0) return sortedEntries.take(count)

    // Center around the user's position
    val halfCount = count / 2
    val start = maxOf(0, index - halfCount)
    val end = minOf(sortedEntries.size, start + count)
    val adjustedStart = maxOf(0, end - count)
    return sortedEntries.subList(adjustedStart, end)
}

/**
 * Format opponent name to abbreviated form (e.g., "Alexander Zverev" -> "A. Zverev")
 */
private fun formatOpponentName(name: String): String {
    val parts = name.split(" ")
    return if (parts.size >= 2) {
        "${parts.first().first()}. ${parts.drop(1).joinToString(" ")}"
    } else {
        name
    }
}

/**
 * Format ISO timestamp to readable time (e.g., "2:30 PM")
 */
private fun formatMatchTime(isoTime: String): String {
    return try {
        val date = js("new Date(isoTime)") as kotlin.js.Date
        val hours = date.getHours()
        val minutes = date.getMinutes()
        val amPm = if (hours >= 12) "PM" else "AM"
        val hour12 = when {
            hours == 0 -> 12
            hours > 12 -> hours - 12
            else -> hours
        }
        "$hour12:${minutes.toString().padStart(2, '0')} $amPm"
    } catch (e: Exception) {
        isoTime
    }
}
